import { useCallback, useEffect, useRef, useContext } from "react";
import { classNames, DomHandler } from "primereact/utils";
import { useLocation } from "react-router-dom";
import { LayoutContext } from "./context/layoutcontext";
import {
  useEventListener,
  useMountEffect,
  useResizeListener,
  useUnmountEffect,
} from "primereact/hooks";
import AppTopbar from "./AppTopbar";
import AppFooter from "./AppFooter";
import AppSearch from "./AppSearch";
import AppBreadCrumb from "./AppBreadCrumb";
import AppRightMenu from "./AppRightMenu";

import { PrimeReactContext } from "primereact/api";
import { Tooltip } from "primereact/tooltip";
import type { ChildContainerProps } from "../types";

const Layout = (props: ChildContainerProps) => {
  const { layoutConfig, layoutState, setLayoutState, isDesktop } =
    useContext(LayoutContext);
  const { setRipple } = useContext(PrimeReactContext);

  const topbarRef = useRef(null);
  const sidebarRef = useRef(null);
  const copyTooltipRef = useRef(null);
  const location = useLocation();
  const pathname = location.pathname;
  const search = location.search;

  // Event Listeners (click + resize)
  const [bindMenuOutsideClickListener, unbindMenuOutsideClickListener] =
    useEventListener({
      type: "click",
      listener: (event) => {
        const isOutsideClicked = !(
          sidebarRef.current?.isSameNode(event.target) ||
          sidebarRef.current?.contains(event.target) ||
          topbarRef.current?.menubutton?.isSameNode(event.target) ||
          topbarRef.current?.menubutton?.contains(event.target)
        );

        if (isOutsideClicked) {
          hideMenu();
        }
      },
    });

  const [bindDocumentResizeListener, unbindDocumentResizeListener] =
    useResizeListener({
      listener: () => {
        if (isDesktop() && !DomHandler.isTouchDevice()) {
          hideMenu();
        }
      },
    });

  // Memoized handlers
  const hideMenu = useCallback(() => {
    setLayoutState((prev) => ({
      ...prev,
      overlayMenuActive: false,
      overlaySubmenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
      menuClick: false,
      resetMenu: false,
    }));
  }, [setLayoutState]);

  const blockBodyScroll = useCallback(() => {
    document.body.classList?.add("blocked-scroll");
  }, []);

  const unblockBodyScroll = useCallback(() => {
    document.body.classList?.remove("blocked-scroll");
  }, []);

  const stableBindMenuOutsideClickListener = useCallback(() => {
    bindMenuOutsideClickListener();
  }, [bindMenuOutsideClickListener]);

  const stableUnbindMenuOutsideClickListener = useCallback(() => {
    unbindMenuOutsideClickListener();
  }, [unbindMenuOutsideClickListener]);

  const stableBindDocumentResizeListener = useCallback(() => {
    bindDocumentResizeListener();
  }, [bindDocumentResizeListener]);

  const stableUnbindDocumentResizeListener = useCallback(() => {
    unbindDocumentResizeListener();
  }, [unbindDocumentResizeListener]);

  // Initial mount: set ripple effect
  useMountEffect(() => {
    setRipple(layoutConfig.ripple);
  });

  // Menu toggle logic
  useEffect(() => {
    const shouldBindClick =
      layoutState.overlayMenuActive ||
      layoutState.staticMenuMobileActive ||
      layoutState.overlaySubmenuActive;

    if (shouldBindClick) {
      stableBindMenuOutsideClickListener();
    }

    if (layoutState.staticMenuMobileActive) {
      blockBodyScroll();
      stableBindDocumentResizeListener();
    }

    return () => {
      stableUnbindMenuOutsideClickListener();
      stableUnbindDocumentResizeListener();
      unblockBodyScroll();
    };
  }, [
    layoutState.overlayMenuActive,
    layoutState.staticMenuMobileActive,
    layoutState.overlaySubmenuActive,
    stableBindMenuOutsideClickListener,
    stableUnbindMenuOutsideClickListener,
    stableBindDocumentResizeListener,
    stableUnbindDocumentResizeListener,
    blockBodyScroll,
    unblockBodyScroll,
  ]);

  // Hide menu on route change
  useEffect(() => {
    hideMenu();
  }, [pathname, search, hideMenu]);

  // Clean up on unmount
  useUnmountEffect(() => {
    stableUnbindMenuOutsideClickListener();
  });

  // Compose layout classes
  const containerClassName = classNames(
    "layout-wrapper",
    {
      "layout-static": layoutConfig.menuMode === "static",
      "layout-overlay-active": layoutState.overlayMenuActive,
      "layout-mobile-active": layoutState.staticMenuMobileActive,
      "layout-static-inactive":
        layoutState.staticMenuDesktopInactive &&
        layoutConfig.menuMode === "static",
      "p-input-filled": layoutConfig.inputStyle === "filled",
      "p-ripple-disabled": !layoutConfig.ripple,
      "layout-sidebar-active": layoutState.sidebarActive,
      "layout-sidebar-anchored": layoutState.anchored,
    },
    "layout-sidebar-darkgray",
    "layout-theme-light"
  );

  return (
    <div className={containerClassName} data-theme="light">
      <Tooltip
        ref={copyTooltipRef}
        target=".block-action-copy"
        position="bottom"
        content="Copied to clipboard"
        event="focus"
      />

      <div className="layout-content-wrapper">
        <AppTopbar ref={topbarRef} sidebarRef={sidebarRef} />
        <div className="layout-content">
          <AppBreadCrumb />
          {props.children}
        </div>
        <AppFooter />
      </div>

      <AppSearch />
      <AppRightMenu />
      <div className="layout-mask"></div>
    </div>
  );
};

export default Layout;
